import { requiredEnv } from './requiredEnv';

/**
 * Velvypal API client
 * Handles velvypal connections and messages
 */

const BACKEND_URL = requiredEnv('REACT_APP_BACKEND_URL');

/**
 * Get user's messages status
 * @param {Object} user - Firebase user object
 * @returns {Promise<{success: boolean, free_messages: number, purchased_messages: number, total_messages: number, last_reset: string}>}
 */
export async function getMessages(user) {
  const idToken = await user.getIdToken();

  const response = await fetch(`${BACKEND_URL}/velvypal/messages`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to fetch messages: ${response.status}`);
  }

  return await response.json();
}

/**
 * Get profile preview for a memory
 * @param {Object} user - Firebase user object
 * @param {string} memoryId - Memory ID to get author's profile
 * @returns {Promise<{success: boolean, profile?: {user_id: string, handle: string, country: string, zodiac_sign: string, languages: string[]}}>}
 */
export async function getProfilePreview(user, memoryId) {
  const idToken = await user.getIdToken();

  const response = await fetch(`${BACKEND_URL}/velvypal/profile-preview/${memoryId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to fetch profile: ${response.status}`);
  }

  return await response.json();
}

/**
 * Get user's velvypal connections
 * @param {Object} user - Firebase user object
 * @returns {Promise<{success: boolean, connections: Array}>}
 */
export async function getConnections(user) {
  const idToken = await user.getIdToken();

  const response = await fetch(`${BACKEND_URL}/velvypal/connections`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to fetch connections: ${response.status}`);
  }

  return await response.json();
}

/**
 * Send a velvynote to start a conversation
 * @param {Object} user - Firebase user object
 * @param {string} memoryId - Memory ID that sparked the conversation
 * @param {string} message - Initial message (1-1000 characters)
 * @returns {Promise<{success: boolean, connection_id?: string, free_messages?: number, purchased_messages?: number, error?: string}>}
 */
export async function sendVelvynote(user, memoryId, message) {
  const idToken = await user.getIdToken();

  const response = await fetch(`${BACKEND_URL}/velvypal/connect`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ memoryId, message }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to send velvynote: ${response.status}`);
  }

  return await response.json();
}

/**
 * Send a message in a connection
 * @param {Object} user - Firebase user object
 * @param {string} connectionId - Connection ID to send message to
 * @param {string} content - Message content (1-1000 characters)
 * @returns {Promise<{success: boolean, message_id?: string, daily_credits?: number, bonus_credits?: number, error?: string}>}
 */
export async function sendMessage(user, connectionId, content) {
  const idToken = await user.getIdToken();

  const response = await fetch(`${BACKEND_URL}/velvypal/message`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ connectionId, content }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to send message: ${response.status}`);
  }

  return await response.json();
}

/**
 * Get messages for a conversation
 * @param {Object} user - Firebase user object
 * @param {string} connectionId - Connection ID to fetch messages for
 * @param {Object} [options] - Pagination options
 * @param {number} [options.limit=50] - Max messages to fetch
 * @param {string} [options.before] - Fetch messages before this timestamp
 * @returns {Promise<{success: boolean, messages: Array, connection?: Object}>}
 */
export async function getConversationMessages(user, connectionId, options = {}) {
  const idToken = await user.getIdToken();

  const params = new URLSearchParams();
  if (options.limit) params.set('limit', options.limit.toString());
  if (options.before) params.set('before', options.before);

  const url = `${BACKEND_URL}/velvypal/conversation/${connectionId}${params.toString() ? '?' + params : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to fetch messages: ${response.status}`);
  }

  return await response.json();
}

/**
 * Mark messages as read
 * @param {Object} user - Firebase user object
 * @param {string} connectionId - Connection ID to mark as read
 * @returns {Promise<{success: boolean, marked_read?: number}>}
 */
export async function markMessagesRead(user, connectionId) {
  const idToken = await user.getIdToken();

  const response = await fetch(`${BACKEND_URL}/velvypal/read/${connectionId}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to mark as read: ${response.status}`);
  }

  return await response.json();
}

/**
 * Purchase messages with gems
 * @param {Object} user - Firebase user object
 * @param {number} messages - Number of messages to purchase
 * @param {number} gems - Gem cost
 * @returns {Promise<{success: boolean, purchased_messages?: number, free_messages?: number, new_gem_balance?: number}>}
 */
export async function purchaseMessages(user, messages, gems) {
  const idToken = await user.getIdToken();

  const response = await fetch(`${BACKEND_URL}/velvypal/purchase-messages`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ messages, gems }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to purchase messages: ${response.status}`);
  }

  return await response.json();
}
